import {
	EditedChip,
	GridWrapper,
	LineItemBackground,
	LineItemErrorMessage,
	LineItemErrorMessageArr,
	LineItemGridContainer
} from "./LineItemGridStyles";
import { FC, ReactNode } from "react";
import LineItemSkeleton from "./LineItemSkeleton";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import { EditedText } from "constants/text.ts";
import Check from "@mui/icons-material/Check";
import { models } from "types/api/viewModels.ts";
import { useUserLocation } from "hooks/useUserLocation.ts";
import { AcknowledgementPriceFormatType } from "types/declarations/enums.ts";

interface LineItemGridProps {
	mainRowContent: ReactNode[];
	isLoading?: boolean;
	className?: string;
	lineItemPricing?: models["LineItemPricingViewModel"];
	modificationRows?: ReactNode[];
	dimensionRows?: ReactNode[];
	requiredCategoriesRows?: ReactNode[];
	error?: string[] | string | null;
	onClick?: () => void;
	"data-id"?: string;
	infoSlot?: ReactNode;
	isSpecialModification?: boolean;
	ackPermission?: AcknowledgementPriceFormatType;
	modifications?: models["ModificationViewModel"][] | null;
}

const LineItemGrid: FC<LineItemGridProps> = ({
	className,
	isLoading = false,
	lineItemPricing,
	mainRowContent,
	modificationRows,
	dimensionRows,
	requiredCategoriesRows,
	error,
	onClick,
	infoSlot,
	isSpecialModification,
	ackPermission,
	modifications,
	...props
}) => {
	const { isNewOrderBuildOrderPage } = useUserLocation();
	const hasModificationRows = modificationRows && modificationRows.length > 0;
	const hasDimensionRows = dimensionRows && dimensionRows.length > 0;
	const hasRequiredCategoriesRows = requiredCategoriesRows && requiredCategoriesRows.length > 0;
	// role is for GA targeting
	const buttonProps = { onClick, "data-id": props["data-id"], ...(onClick && { role: "button" }) };
	const errorArr = Array.isArray(error) ? error : [error].filter(Boolean);

	return (
		<GridWrapper
			{...buttonProps}
			data-testid="line-item-grid-wrapper"
		>
			<LineItemSkeleton isLoading={isLoading} />
			<LineItemBackground
				isLoading={isLoading}
				variant="outlined"
			>
				{infoSlot}
				{errorArr.length > 0 && errorArr[0] !== EditedText && (
					<LineItemErrorMessageArr>
						{errorArr?.map((err) => (
							<LineItemErrorMessage
								key={err}
								data-testid="line-item-error-message"
							>
								<ErrorOutline />
								<span>{err}</span>
							</LineItemErrorMessage>
						))}
					</LineItemErrorMessageArr>
				)}
				{errorArr[0] === EditedText && (
					<EditedChip
						label={EditedText}
						icon={<Check />}
						size="small"
					/>
				)}
				<LineItemGridContainer
					className={className}
					mainContentColumnCount={mainRowContent.length}
					isNewOrderBuildOrderPage={isNewOrderBuildOrderPage}
					isSpecialModification={isSpecialModification}
				>
					{mainRowContent.length > 0 && <div>{mainRowContent}</div>}
					{(hasModificationRows || hasDimensionRows || requiredCategoriesRows) && (
						<div>
							{hasModificationRows && modificationRows}
							{hasDimensionRows && dimensionRows}
							{hasRequiredCategoriesRows && requiredCategoriesRows}
						</div>
					)}
				</LineItemGridContainer>
			</LineItemBackground>
		</GridWrapper>
	);
};

export default LineItemGrid;
